import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

export default class extends Controller {
  static targets = ["dropZone"];
  static values = {
    urlTemplate: String,
    categoryId: String
  };

  connect() {
    console.debug("Text Question Drag & Drop controller connected (SortableJS)");
    this.sortables = [];
    this.initializeSortable();
  }

  disconnect() {
    this.sortables.forEach(sortable => sortable.destroy());
  }

  initializeSortable() {
    this.dropZoneTargets.forEach(zone => {
      const sortable = new Sortable(zone, {
        group: "shared",
        animation: 150,
        ghostClass: "bg-blue-100",
        draggable: "[data-question-id]",
        emptyInsertThreshold: 80,
        onEnd: this.onEnd.bind(this)
      });
      this.sortables.push(sortable);
    });
  }

  onEnd(event) {
    const { item, to, from, oldIndex, newIndex } = event;

    // If dropped in the same list, we don't need to do anything (unless we implement reordering later)
    if (to === from) {
      return;
    }

    const questionId = item.dataset.questionId;
    const targetQuality = to.dataset.quality;
    const sourceQuality = from.dataset.quality;

    console.debug("Dropped", {
      questionId,
      sourceQuality,
      targetQuality
    });

    this.handleEmptyStates(to, from);
    this.updateCounts();
    this.updateQuestionQuality(questionId, targetQuality, item, from);
  }

  handleEmptyStates(toContainer, fromContainer) {
    // Remove empty state from target if it exists
    const targetEmptyState = toContainer.querySelector(".text-center.py-8");
    if (targetEmptyState) {
      targetEmptyState.remove();
    }

    // Check if source container is now empty (ignoring the dragged item which is already moved by Sortable)
    // Sortable moves the item, so we check remaining children
    const fromItems = Array.from(fromContainer.children).filter(
      child => !child.classList.contains("text-center")
    );

    if (fromItems.length === 0) {
      // Add empty state (match _questions_grid.html.erb)
      const emptyStateHtml = `
        <div class="flex flex-1 flex-col items-center justify-center py-8 min-h-0 text-gray-400 text-center pointer-events-none select-none">
          <svg class="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p class="mt-2 text-sm">Drop statements here</p>
        </div>
      `;
      fromContainer.insertAdjacentHTML('beforeend', emptyStateHtml);
    }
  }

  updateCounts() {
    this.dropZoneTargets.forEach(zone => {
      // Count actual question elements (not the empty state)
      const count = Array.from(zone.children).filter(
        child => !child.classList.contains("text-center")
      ).length;

      // Find and update the count badge in the header
      // The header is in the parent container's previous sibling or part of the parent structure
      // Structure: .flex-col > header > .flex-1 (contains zone)
      const columnContainer = zone.closest(".flex.flex-col");
      if (columnContainer) {
        const headerSpan = columnContainer.querySelector("h3 span");
        if (headerSpan) {
          headerSpan.textContent = count;
        }
      }
    });
  }

  async updateQuestionQuality(questionId, quality, itemElement, originalContainer) {
    try {
      // Get the CSRF token
      const token = document.querySelector('meta[name="csrf-token"]').content;

      // Construct URL using template and values
      let url = this.urlTemplateValue
        .replace(':category_id', this.categoryIdValue)
        .replace(':id', questionId);

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "application/json"
        },
        body: JSON.stringify({ quality })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.debug("Quality updated successfully", data);

      // Show a subtle success indicator
      this.showSuccessIndicator(itemElement);

    } catch (error) {
      console.error("Error updating question quality:", error);

      // Revert visual change
      originalContainer.appendChild(itemElement);
      this.handleEmptyStates(originalContainer, itemElement.parentElement); // Re-adjust empty states
      this.updateCounts();

      // Show error message (using a more subtle alert for now, could be improved to a toast)
      alert("Failed to update statement quality. The change has been reverted.");
    }
  }

  showSuccessIndicator(element) {
    // Add a temporary success border
    element.classList.add("ring-2", "ring-green-500");

    setTimeout(() => {
      element.classList.remove("ring-2", "ring-green-500");
    }, 1000);
  }
}
